/**
 * Trunnion AI - Agent Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { agentsApi } from '@/api/agents.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { CreateAgentRequest, UpdateAgentRequest } from '@/types';

export function useAgentList(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: queryKeys.agents.list(filters),
    queryFn: () => agentsApi.list(filters),
  });
}

export function useAgent(id: string) {
  return useQuery({
    queryKey: queryKeys.agents.detail(id),
    queryFn: () => agentsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (agent: CreateAgentRequest) => agentsApi.create(agent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      addNotification({
        type: 'success',
        title: 'Agent Created',
        message: 'Agent has been created successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message,
      });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentRequest }) =>
      agentsApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.agents.detail(variables.id),
      });
      addNotification({
        type: 'success',
        title: 'Agent Updated',
        message: 'Agent has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message,
      });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (id: string) => agentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      addNotification({
        type: 'success',
        title: 'Agent Deleted',
        message: 'Agent has been deleted.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message,
      });
    },
  });
}
